/**
 * Searchlights (02 §9.2): lit only at night and at dusk. Each sweeps an arc to the south of
 * its pole; a ranger caught in the beam is seen by every soldier as if it were day
 * (`rangerLit`, read by `enemies/perception.ts`), and held in it for a moment he is called
 * in. Smoke, solid cover, a trench crawled along and water over his head all hide him from
 * it. Shoot the lamp (`projectiles.ts`) or blow the pole down and it is dark for good.
 *
 * The renderer draws each beam from `world.hazards.searchlights` (`angle`, `range`, `halfAngle`, `on`).
 */

import { bark, raiseAlert, sendToInvestigate } from '../alert';
import { type Ctx, isFighter } from '../ctx';
import { isAutomated } from '../enemies/specials/kinds';
import { angleDiff, atan2, PI } from '../math';
import { clearSight } from '../smoke';
import { SEARCHLIGHT } from '../tuning';
import { DT, type Enemy, type MissionDef, type Searchlight } from '../types';
import { rangerOnFoot, rangerSubmerged } from './terrain';

const DEG = PI / 180;

/** One per searchlight prop - and none at all by daylight. Lamps start out of step, by prop id. */
export function createSearchlights(def: MissionDef): Searchlight[] {
  if (def.timeOfDay !== 'night' && def.timeOfDay !== 'dusk') return [];
  const half = (SEARCHLIGHT.arcDeg / 2) * DEG;
  const lights: Searchlight[] = [];
  for (const prop of def.props) {
    if (prop.kind !== 'searchlight') continue;
    const phase = ((prop.id * 37) % 100) / 100;
    lights.push({
      propId: prop.id, x: prop.x, z: prop.z,
      angle: prop.yaw - half + phase * half * 2,
      sweepFrom: prop.yaw - half, sweepTo: prop.yaw + half,
      dir: prop.id % 2 === 0 ? 1 : -1,
      range: SEARCHLIGHT.range, halfAngle: SEARCHLIGHT.halfAngleDeg * DEG, on: true,
    });
  }
  return lights;
}

/** Is this point inside the beam, with nothing - cover or smoke - between it and the lamp? */
export function beamReaches(
  ctx: Pick<Ctx, 'world' | 'statics'>,
  s: Searchlight,
  x: number,
  z: number,
  h: number,
): boolean {
  if (!s.on) return false;
  const dx = x - s.x;
  const dz = z - s.z;
  if (dx * dx + dz * dz > s.range * s.range) return false;
  if (Math.abs(angleDiff(s.angle, atan2(dx, dz))) > s.halfAngle) return false;
  return clearSight(ctx, s.x, s.z, SEARCHLIGHT.lampHeight, x, z, h, s.propId);
}

function nearestSoldier(ctx: Ctx, x: number, z: number): Enemy | null {
  let best: Enemy | null = null;
  let bestD = Infinity;
  for (const e of ctx.world.enemies) {
    if (!isFighter(e) || e.state === 'down' || isAutomated(e.kind)) continue;
    const d = (e.x - x) * (e.x - x) + (e.z - z) * (e.z - z);
    if (d < bestD) {
      bestD = d;
      best = e;
    }
  }
  return best;
}

export function stepSearchlights(ctx: Ctx): void {
  const w = ctx.world;
  const h = w.hazards;
  if (h.searchlights.length === 0) return;
  const r = w.ranger;
  // The sacred rule holds for the lamp crew too: crawling in a trench, or under water, he is not there.
  const exposed = rangerOnFoot(w) && !rangerSubmerged(w) && !(r.inTrench && r.stance === 'crawl');
  const height = r.stance === 'crawl' ? 0.4 : 1.5;
  let lit = false;
  for (const s of h.searchlights) {
    if (!s.on) continue;
    if (w.props.find((p) => p.id === s.propId)?.destroyed !== false) {
      s.on = false;
      continue;
    }
    s.angle += s.dir * SEARCHLIGHT.sweepDegPerSecond * DEG * DT;
    if (s.angle >= s.sweepTo) {
      s.angle = s.sweepTo;
      s.dir = -1;
    } else if (s.angle <= s.sweepFrom) {
      s.angle = s.sweepFrom;
      s.dir = 1;
    }
    if (exposed && !lit && beamReaches(ctx, s, r.x, r.z, height)) lit = true;
  }
  h.litTicks = lit ? h.litTicks + 1 : 0;
  // Once per spell in the light. A man in their own uniform is just a man crossing the yard.
  if (h.litTicks !== SEARCHLIGHT.alarmTicks || w.objective.disguised) return;
  raiseAlert(ctx, 1, r.x, r.z);
  const man = nearestSoldier(ctx, r.x, r.z);
  if (!man) return;
  bark(ctx, man, 'contact');
  sendToInvestigate(man, r.x, r.z, true);
}
